import React from 'react';
import { Navigate } from 'react-router-dom';
import { Lock, MessageCircle } from 'lucide-react';
import { useRyanAccess } from '../hooks/useRyanAccess';
import RyanHeader from './RyanHeader';

// Mesmo número padrão usado como fallback em outros lugares da plataforma
// (RyanHomePage.tsx/RyanFieldPage.tsx DEFAULT_CONFIG) — não dá pra buscar o
// RyanConfig configurado pelo admin porque essa rota exige acesso Ryan, que é
// justamente o que falta aqui.
const SUPPORT_WHATSAPP_NUMBER = '5577988362587';

function buildSupportWhatsappUrl(): string {
  const cleaned = SUPPORT_WHATSAPP_NUMBER.replace(/\D/g, '');
  const full = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
  const msg = 'Oi! Tentei acessar a GRAIA Medicina mas não consegui. Pode me ajudar?';
  return `https://wa.me/${full}?text=${encodeURIComponent(msg)}`;
}

interface RyanProtectedRouteProps {
  children: React.ReactNode;
}

const RyanProtectedRoute: React.FC<RyanProtectedRouteProps> = ({ children }) => {
  const { hasRyanAccess, ryanAccessLoading } = useRyanAccess();
  const token = localStorage.getItem('accessToken');

  if (ryanAccessLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bg-page)',
      }}>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: '3px solid rgba(13,59,14,0.15)',
          borderTopColor: '#0D3B0E',
          animation: 'spin 0.7s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/ryan/login" replace />;
  }

  if (!hasRyanAccess) {
    return (
      <div style={{ background: 'var(--bg-page)', minHeight: '100vh' }}>
        <RyanHeader showLogout={false} />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          paddingTop: 24,
        }}>
          <div style={{
            background: 'var(--bg-card, #fff)',
            borderRadius: 12,
            border: '1px solid rgba(47,113,48,0.15)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            padding: '48px 40px',
            maxWidth: 380,
            width: '90%',
            textAlign: 'center',
          }}>
            <Lock size={40} color="#0D3B0E" style={{ marginBottom: 16 }} />
            <h2 style={{
              fontSize: 20,
              fontWeight: 700,
              color: 'var(--text-primary, #000F00)',
              margin: '0 0 12px',
            }}>
              Quase lá!
            </h2>
            <p style={{
              fontSize: 14,
              color: 'var(--text-secondary, #6B7280)',
              lineHeight: 1.6,
              margin: '0 0 28px',
            }}>
              Não encontramos uma conta com acesso à GRAIA Medicina com esses dados.
              Se você já comprou algum material, fale com a gente que resolvemos rapidinho.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <a
                href={buildSupportWhatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  background: '#0D3B0E',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 28px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  textDecoration: 'none',
                }}
              >
                <MessageCircle size={15} /> Falar no WhatsApp
              </a>
              <button
                onClick={() => window.history.back()}
                style={{
                  background: 'transparent',
                  color: 'var(--text-secondary, #6B7280)',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 28px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  textDecoration: 'underline',
                }}
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RyanProtectedRoute;
